import React, { useRef } from 'react';
import AliceCarousel from 'react-alice-carousel';

import { useTranslation } from 'next-i18next/pages';

import { Button } from 'antd';
import cn from 'classnames';

import { Container } from '@//components/common';
import { STORAGE_SEARCH_URL } from '@/constants/routes';
import { useUserState } from '@/context/user.context';
import useMobile from '@/hooks/use-mobile';
import { TranslationNamespace } from '@/i18n';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

import mapDesktop from './assets/map-desktop.svg';
import mapMobile from './assets/map-mobile.svg';
import StatisticTooltip from './StatisticTooltip';

import 'react-alice-carousel/lib/scss/alice-carousel.scss';
import styles from './WarehousesStatistic.module.scss';

type WarehousesCityStatistic = {
	title: string;
	warehousesCount: number;
	workloadPercent: number;
	desktopPosition: { left?: string; top?: string };
	mobilePosition?: { left?: string; top?: string; display?: 'none' };
};

export default function WarehousesStatistic() {
	const { isMobile } = useMobile();
	const sliderRef = useRef<AliceCarousel>();
	const t = useTranslation(TranslationNamespace.Marketing).t;

	return (
		<section className={styles.section}>
			<Container className={styles.mainContainer}>
				<div
					className={styles.map}
					style={{ backgroundImage: `url(${isMobile ? mapMobile.src : mapDesktop.src})` }}
				>
					{warehouses.map((warehouse, index) => (
						<StatisticTooltip
							key={index}
							title={t(warehouse.title)}
							warehousesCount={warehouse.warehousesCount}
							workloadPercent={warehouse.workloadPercent}
							style={isMobile ? warehouse.mobilePosition : warehouse.desktopPosition}
						/>
					))}
				</div>

				<div className={styles.textContainer}>
					<h2 className={styles.title}>
						{t(
							'main-landing.warehouse-statistics.we-work-with-the-majority-of-the-warehouses-in-russia',
						)}
					</h2>
					<p className={styles.subtitle}>
						{t('main-landing.warehouse-statistics.we-re-expanding-the-geography-of-our-presence')}
					</p>
					<Button
						className={styles.seeOnMapButton}
						type="primary"
						href={`${STORAGE_SEARCH_URL}?map=open`}
					>
						{t('main-landing.warehouse-statistics.see-on-the-map')}
					</Button>
				</div>
			</Container>

			<Container>
				<div className={styles.workWith}>
					<p className={styles.workWithTitle}>
						{t('main-landing.warehouse-statistics.our-customers-include')}
					</p>

					<div className={styles.workWithSlider}>
						<Button
							type="primary"
							shape="circle"
							size="large"
							icon={<LeftOutlined />}
							className={cn(styles.sliderButton, styles.prev)}
							onClick={(e) => sliderRef?.current?.slidePrev(e)}
						/>
						{/* <SliderButton
							direction="prev"
							className={cn(styles.sliderButton, styles.prev)}
							onClick={(e) => sliderRef?.current?.slidePrev(e)}
						/> */}
						<AliceCarousel
							ref={sliderRef}
							autoWidth
							infinite
							disableDotsControls
							disableButtonsControls
							mouseTracking
							items={workWith.map((text, index) => (
								<div key={index} className={styles.workWithItem}>
									{t(text)}
								</div>
							))}
						/>
						<Button
							type="primary"
							shape="circle"
							size="large"
							icon={<RightOutlined />}
							className={cn(styles.sliderButton, styles.next)}
							onClick={(e) => sliderRef?.current?.slideNext(e)}
						/>
					</div>
				</div>
			</Container>
		</section>
	);
}

const workWith: string[] = [
	'main-landing.warehouse-statistics.dgp',
	'main-landing.warehouse-statistics.vladmorrybport',
	'main-landing.warehouse-statistics.dalkomholod',
	'main-landing.warehouse-statistics.sibirsky-holod',
	'main-landing.warehouse-statistics.agama',
	'main-landing.warehouse-statistics.sk-evraziya',
	'main-landing.warehouse-statistics.vikta',
	'main-landing.warehouse-statistics.dobroflot',
	'main-landing.warehouse-statistics.koryak-more-produkt',
];

const warehouses: WarehousesCityStatistic[] = [
	{
		title: 'main-landing.warehouse-statistics.moscow',
		warehousesCount: 8,
		workloadPercent: 70,
		desktopPosition: { top: '36rem', left: '10.5rem' },
		mobilePosition: { display: 'none' },
	},
	{
		title: 'main-landing.warehouse-statistics.murmansk',
		warehousesCount: 12,
		workloadPercent: 50,
		desktopPosition: { top: '2rem', left: '30rem' },
		mobilePosition: { display: 'none' },
	},
	{
		title: 'main-landing.warehouse-statistics.arkhangelsk',
		warehousesCount: 10,
		workloadPercent: 64,
		desktopPosition: { top: '16rem', left: '42rem' },
		mobilePosition: { display: 'none' },
	},
	{
		title: 'main-landing.warehouse-statistics.novosibirsk',
		warehousesCount: 14,
		workloadPercent: 80,
		desktopPosition: { top: '31.5rem', left: '40rem' },
		mobilePosition: { display: 'none' },
	},
	{
		title: 'main-landing.warehouse-statistics.irkutsk',
		warehousesCount: 9,
		workloadPercent: 76,
		desktopPosition: { top: '33rem', left: '67rem' },
		mobilePosition: { display: 'none' },
	},
	{
		title: 'main-landing.warehouse-statistics.petropavlovsk-kamchatsky',
		warehousesCount: 8,
		workloadPercent: 20,
		desktopPosition: { left: '96.5rem', top: '12rem' },
		mobilePosition: { left: '6rem', top: '5.5rem' },
	},
	{
		title: 'main-landing.warehouse-statistics.vladivostok',
		warehousesCount: 25,
		workloadPercent: 50,
		desktopPosition: { left: '92.5rem', top: '34.8rem' },
		mobilePosition: { left: '13.5rem', top: '25.5rem' },
	},
];
